import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ShoppingBag, Home, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { startPayment } from "@/lib/api";
import { WHATSAPP_URL, WhatsAppIcon } from "@/components/WhatsApp";

const SHOP_PHOTOS = {
  dumbbells: "https://customer-assets-v7afamib.emergentagent.net/job_gym-packages-1/artifacts/9b705253477d4bd1_Premium%20showroom%20with%20vibrant%20yellow%20accents.png",
  treadmills: "https://customer-assets-v7afamib.emergentagent.net/job_gym-packages-1/artifacts/10760a8de9ca5e49_Polished%20showroom%20with%20bold%20yellow%20accents.png",
  accessories: "https://customer-assets-v7afamib.emergentagent.net/job_gym-packages-1/artifacts/013b081fd5ecac21_Premium%20showroom%20with%20vibrant%20accents.png",
  shoes: "https://customer-assets-v7afamib.emergentagent.net/job_gym-packages-1/artifacts/775b43319f65ef26_Premium%20commercial%20showroom%20with%20vibrant%20accents.png",
};

const ITEMS = [
  {
    img: SHOP_PHOTOS.dumbbells,
    name_en: "Dumbbell Set", name_bn: "ডাম্বেল সেট",
    spec_en: "Fixed rubber dumbbells, 2.5–30 kg pairs", spec_bn: "ফিক্সড রাবার ডাম্বেল, ২.৫–৩০ কেজি জোড়া",
  },
  {
    img: SHOP_PHOTOS.treadmills,
    name_en: "Treadmills", name_bn: "ট্রেডমিল",
    spec_en: "Home & gym-grade models in stock", spec_bn: "হোম ও জিম-গ্রেড মডেল স্টকে আছে",
  },
  {
    img: SHOP_PHOTOS.accessories,
    name_en: "Cable Attachments & Accessories", name_bn: "অ্যাটাচমেন্ট ও অ্যাক্সেসরিজ",
    spec_en: "Ropes, handles, bands, ab wheels & more", spec_bn: "রোপ, হ্যান্ডেল, ব্যান্ড, অ্যাব হুইল সহ আরও অনেক কিছু",
  },
  {
    img: SHOP_PHOTOS.shoes,
    name_en: "Training Shoes & Gear", name_bn: "ট্রেনিং জুতা ও গিয়ার",
    spec_en: "Branded training shoes & gym gear", spec_bn: "ব্র্যান্ডেড ট্রেনিং জুতা ও জিম গিয়ার",
  },
];

const MAPS_URL = "https://maps.app.goo.gl/2cd3snEkAZvxYjDN8";

const Equipments = ({ paymentsEnabled }) => {
  const { lang, t } = useLang();
  const [booking, setBooking] = useState(null);

  const book = (item, i) => {
    if (booking !== null) return;
    setBooking(i);
    startPayment({
      type: "equipment",
      itemName: item.name_en,
      onSuccess: () => {
        setBooking(null);
        toast.success(t.book_success);
      },
      onError: () => {
        setBooking(null);
        toast.error(t.payment_failed);
      },
    });
  };

  return (
    <section id="equipments" className="relative py-24 sm:py-32" data-testid="equipments-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16 flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-volt" data-testid="shop-kicker">
              — {t.shop_kicker}
            </p>
            <h2 className="sr-only">{t.shop_title}</h2>
            <img
              src="https://customer-assets-v7afamib.emergentagent.net/job_gym-packages-1/artifacts/62524ba93583741a_ChatGPT%20Image%20Sep%2022%2C%202026%2C%2012_40_37%20AM.png"
              alt="Fitnastic Gym Equipments"
              data-testid="shop-logo"
              className="w-72 rounded-lg sm:w-96"
            />
            <p className="mt-4 max-w-xl text-base text-zinc-400 sm:text-lg" data-testid="shop-subtitle">{t.shop_sub}</p>
          </div>
          <div className="shrink-0">
            <a
              data-testid="shop-visit-button"
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 rounded-full border border-volt/50 px-6 py-3.5 font-display text-sm font-bold uppercase tracking-widest text-volt transition-colors duration-200 hover:bg-volt hover:text-ink"
            >
              <MapPin className="h-4 w-4" />
              {t.shop_visit_cta}
            </a>
            <p className="mt-3 max-w-xs text-xs leading-relaxed text-zinc-500" data-testid="shop-address">{t.shop_address}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-10 flex items-center gap-3.5 rounded-xl border border-volt/30 bg-volt/10 px-5 py-4"
          data-testid="shop-home-note"
        >
          <Home className="h-6 w-6 shrink-0 text-volt" />
          <p className="text-sm font-semibold text-volt sm:text-base">{t.shop_home_note}</p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="shop-grid">
          {ITEMS.map((item, i) => (
            <motion.div
              key={item.name_en}
              data-testid={`shop-item-${i}`}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: (i % 4) * 0.08 }}
              className="card-scan group overflow-hidden rounded-2xl border border-zinc-800 bg-surface transition-all duration-300 hover:-translate-y-1.5 hover:border-volt/40"
            >
              <div className="relative aspect-[3/4] overflow-hidden">
                <img
                  src={item.img}
                  alt={item.name_en}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-ink/80 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-volt backdrop-blur-sm">
                  <ShoppingBag className="h-3 w-3" />
                  Fitnastic Equipments
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl font-bold uppercase tracking-wide text-white" data-testid={`shop-item-name-${i}`}>
                  {lang === "en" ? item.name_en : item.name_bn}
                </h3>
                <p className="mt-1 text-sm text-zinc-500">{lang === "en" ? item.spec_en : item.spec_bn}</p>
                <div className="mt-5 flex items-center justify-between gap-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{t.shop_price_note}</span>
                  <a
                    data-testid={`shop-enquire-button-${i}`}
                    href={`${WHATSAPP_URL}?text=${encodeURIComponent(`Hi Fitnastic! I want to buy: ${item.name_en}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex shrink-0 items-center gap-1.5 rounded-full bg-[#25D366]/15 px-4 py-2 font-display text-xs font-bold uppercase tracking-widest text-[#25D366] transition-colors duration-200 hover:bg-[#25D366] hover:text-ink"
                  >
                    <WhatsAppIcon className="h-3.5 w-3.5" />
                    {t.shop_enquire}
                  </a>
                </div>
                {paymentsEnabled && (
                  <button
                    data-testid={`shop-book-button-${i}`}
                    onClick={() => book(item, i)}
                    disabled={booking !== null}
                    className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-volt/60 py-2.5 font-display text-xs font-bold uppercase tracking-widest text-volt transition-colors duration-200 hover:bg-volt hover:text-ink disabled:opacity-60"
                  >
                    {booking === i ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />}
                    {t.book_advance}
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Equipments;
